/** tm/legal/legalApi.ts — 법령·판례·뉴스 API 래퍼 (/api/tm/legal) */
import { req, apiBase, apiToken } from '../api';
import type {
  Topic, FeedQuery, FeedPage, LegalItemDetail, NewsDetail, LegalAlerts, LegalSummary, LegalDigest,
  CompanyLegalItem, LegalKeyword, KeywordInput, WatchedLaw, LegalSettings, TestSmsResult, LegalSource,
  SynonymGroups, SynonymData, UpcomingRow, ReadAllIds, LinkedCompany, NewsStatus, BillDetail, BillsStatus, NoticeDetail, SavedKeyword,
  CabinetDetail, GovProgress, ItemKind, LegalMemoFile,
} from './legalTypes';
import { keepNewToken } from '../../tokenRefresh';

const P = '/legal';

function qs(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter((e): e is [string, string | number] => e[1] != null && e[1] !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  return parts.length ? '?' + parts.join('&') : '';
}

/** 목록 응답이 배열이든 { rows } 든 배열로 정규화 */
function rowsOf<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'rows' in data) {
    const rows = (data as { rows: unknown }).rows;
    if (Array.isArray(rows)) return rows as T[];
  }
  return [];
}
const get = <T>(path: string) => req<T>('GET', P + path).then(r => r.data);
const list = <T>(path: string) => req<unknown>('GET', P + path).then(r => rowsOf<T>(r.data));

/** /synonyms 응답을 [[동의어...]] 로 정규화 — 배열·{groups}·{rows}·{단어:[동의어]} 모두 허용 */
function toGroups(data: unknown): SynonymGroups {
  const isStrArr = (v: unknown): v is string[] => Array.isArray(v) && v.every(x => typeof x === 'string');
  let src: unknown = data;
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    const o = src as Record<string, unknown>;
    if (Array.isArray(o.groups)) src = o.groups;
    else if (Array.isArray(o.rows)) src = o.rows;
    else return Object.entries(o).filter(([, v]) => isStrArr(v)).map(([k, v]) => [k, ...(v as string[])]);
  }
  if (!Array.isArray(src)) return [];
  return src.map(g => (isStrArr(g) ? g : g && typeof g === 'object' && isStrArr((g as { terms?: unknown }).terms) ? (g as { terms: string[] }).terms : []))
    .filter(g => g.length > 1);
}

// ── 조회 ──
export const getTopics = () => list<Topic>('/topics');
export const getFeed = (q: FeedQuery, page: number, pageSize = 30) =>
  get<FeedPage>(`/feed${qs({
    topic: q.topic, tab: q.tab, sub: q.sub || undefined, q: q.q.trim() || undefined,
    onlyNew: q.onlyNew ? 1 : undefined, starred: q.starred ? 1 : undefined,
    lawKind: q.tab === 'law' && q.lawKind ? q.lawKind : undefined, page, pageSize,
  })}`);
export const getItem = (id: number) => get<LegalItemDetail>(`/items/${id}`);
export const getNews = (id: number) => get<NewsDetail>(`/news/${id}`);
export const getAlerts = () => get<LegalAlerts>('/alerts');
/** 모두 확인 → 되돌리기용 id (서버가 주지 않으면 null) */
export const readAllAlerts = () => req<{ updated?: number; ids?: ReadAllIds } | null>('POST', `${P}/alerts/read-all`).then(r => r.data?.ids || null);
export const unreadAlerts = (ids: ReadAllIds) => req<{ restored: number }>('POST', `${P}/alerts/unread`, ids);
/** 「곧 시행」 — 같은 법령·같은 시행일은 한 줄 (서버에 없으면 null → 화면에서 직접 묶음) */
export const getUpcoming = (label: string) => get<{ rows: UpcomingRow[] }>(`/topics/${encodeURIComponent(label)}/upcoming`).then(d => d?.rows || [], () => null);
export const getSummary = () => get<LegalSummary>('/summary');
export const getBill = (id: number) => get<BillDetail>(`/bills/${id}`);
export const getBillsStatus = () => get<BillsStatus>('/bills/status');
export const setBillRead = (id: number, read: boolean) => req<unknown>('POST', `${P}/bills/${id}/read`, { read });
export const setBillStar = (id: number, starred: boolean) => req<unknown>('POST', `${P}/bills/${id}/star`, { starred });
export const getNewsStatus = () => get<NewsStatus>('/news/status');
export const getNotice = (id: number) => get<NoticeDetail>(`/notices/${id}`);
export const setNoticeRead = (id: number, read: boolean) => req<unknown>('POST', `${P}/notices/${id}/read`, { read });
export const setNoticeStar = (id: number, starred: boolean) => req<unknown>('POST', `${P}/notices/${id}/star`, { starred });
export const getCabinet = (id: number) => get<CabinetDetail>(`/cabinet/${id}`);
export const setCabinetRead = (id: number, read: boolean) => req<unknown>('POST', `${P}/cabinet/${id}/read`, { read });
export const setCabinetStar = (id: number, starred: boolean) => req<unknown>('POST', `${P}/cabinet/${id}/star`, { starred });
/** 같은 법령안의 진행 단계 (입법예고 → 국무회의 → 국회 → 공포 → 시행) — 이어진 정부입법현황이 없으면 null */
export const getProgress = (kind: Exclude<ItemKind, 'news'>, id: number) => get<GovProgress | null>(`/cabinet/progress${qs({ kind, id })}`);
export const getDigest = () => get<LegalDigest>('/digest');
export const getCompanyItems = (companyId: string) => list<CompanyLegalItem>(`/companies/${encodeURIComponent(companyId)}/items${qs({ pageSize: 50 })}`);

// ── 개인 표시 ──
export const setItemRead = (id: number, read: boolean) => req<unknown>('POST', `${P}/items/${id}/read`, { read });
export const setItemStar = (id: number, starred: boolean) => req<unknown>('POST', `${P}/items/${id}/star`, { starred });
export const saveMemo = (id: number, memo: string) => req<unknown>('PUT', `${P}/items/${id}/memo`, { memo });
/** 내 제목 — 빈 문자열이면 원래 제목으로 (응답 custom_title = 서버가 정리한 값, null = 원래 제목) */
export const saveItemTitle = (id: number, title: string) =>
  req<{ id: number; custom_title: string | null }>('PUT', `${P}/items/${id}/title`, { title }).then(r => r.data);
export const setNewsRead = (id: number) => req<unknown>('POST', `${P}/news/${id}/read`, {});
export const setNewsStar = (id: number, starred: boolean) => req<unknown>('POST', `${P}/news/${id}/star`, { starred });
/** 금융위 · 신보 · 기보 글 → 공고 검토함(★ 심층 분석) — 2026-10-08 */
export const sendNewsToGov = (url: string) => req<{ groupId: string; existed: boolean; title: string }>('POST', '/gov-feed/from-news', { url });

// ── 내 메모 첨부 파일 (본인만) ──
export const getMemoFiles = (id: number) => list<LegalMemoFile>(`/items/${id}/files`);
/** 15분 다운로드 주소 */
export const getMemoFileUrl = (id: number, fileId: string) =>
  get<{ url: string; name: string }>(`/items/${id}/files/${encodeURIComponent(fileId)}/download`);
export const deleteMemoFile = (id: number, fileId: string) => req<unknown>('DELETE', `${P}/items/${id}/files/${encodeURIComponent(fileId)}`);
/** 파일 1개 업로드 (진행률 0~1 콜백) — fetch 는 업로드 진행률을 못 주므로 XHR. 서버는 한 번에 10개까지 받지만
 *  nginx 본문 한도(50MB) 안에 들도록 화면은 1개씩 보낸다 */
export function uploadMemoFile(id: number, file: File, onProgress?: (ratio: number) => void): Promise<LegalMemoFile> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase()}${P}/items/${id}/files`);
    xhr.setRequestHeader('Authorization', 'Bearer ' + apiToken());
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onerror = () => reject(new Error('Failed to fetch'));
    xhr.onabort = () => reject(new Error('aborted'));
    xhr.onload = () => {
      let json: { success?: boolean; data?: { rows?: LegalMemoFile[] }; error?: string } = {};
      try { json = JSON.parse(xhr.responseText || '{}'); } catch { json = {}; }
      const row = json.data?.rows?.[0];
      if (xhr.status === 413) reject(new Error('파일이 너무 큽니다 (30MB 이하)'));
      else if (xhr.status >= 200 && xhr.status < 300 && json.success !== false && row) {
        const tk = xhr.getResponseHeader('X-New-Token');
        keepNewToken(tk);
        resolve(row);
      } else reject(new Error(json.error || `${xhr.status} 업로드 실패`));
    };
    const fd = new FormData();
    fd.append('files', file, file.name);
    xhr.send(fd);
  });
}

// ── 업체 연결 ──
export const linkCompany = (id: number, companyId: string) =>
  req<{ id: number; companies: LinkedCompany[] }>('POST', `${P}/items/${id}/companies`, { companyId }).then(r => r.data);
export const unlinkCompany = (id: number, companyId: string) =>
  req<unknown>('DELETE', `${P}/items/${id}/companies/${encodeURIComponent(companyId)}`);

// ── 관리 (super) ──
export const getKeywords = () => list<LegalKeyword>('/keywords');
export const createKeyword = (d: KeywordInput) => req<SavedKeyword>('POST', `${P}/keywords`, d).then(r => r.data);
export const updateKeyword = (id: number, d: Partial<KeywordInput>) => req<SavedKeyword>('PUT', `${P}/keywords/${id}`, d).then(r => r.data);
export const deleteKeyword = (id: number) => req<unknown>('DELETE', `${P}/keywords/${id}`);
export const getSynonyms = () => req<unknown>('GET', `${P}/synonyms`).then((r): SynonymData => {
  const o = r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? (r.data as { abbr?: unknown; sets?: unknown }) : null;
  const keys = o?.abbr && typeof o.abbr === 'object' ? Object.keys(o.abbr) : [];
  // sets[i].abbr = 약칭(뉴스 검색어에만) — 제목·본문·법안 검색어에는 full 만 제안
  const fromSets = Array.isArray(o?.sets) ? o.sets.flatMap(x => (x && typeof x === 'object' && Array.isArray((x as { abbr?: unknown }).abbr) ? (x as { abbr: unknown[] }).abbr.filter((w): w is string => typeof w === 'string') : [])) : [];
  return { groups: toGroups(r.data), abbr: Array.from(new Set([...keys, ...fromSets])) };
});
export const getSources = () => list<LegalSource>('/sources');

export const getLaws = () => list<WatchedLaw>('/laws');
export const createLaw = (name: string) => req<WatchedLaw>('POST', `${P}/laws`, { name }).then(r => r.data);
export const updateLaw = (id: number, d: Partial<Pick<WatchedLaw, 'active' | 'sort'>>) => req<WatchedLaw>('PUT', `${P}/laws/${id}`, d).then(r => r.data);
export const deleteLaw = (id: number) => req<unknown>('DELETE', `${P}/laws/${id}`);

export const getSettings = () => get<LegalSettings>('/settings');
export const saveSettings = (d: Omit<LegalSettings, 'lastSmsDate'>) => req<LegalSettings>('PUT', `${P}/settings`, d).then(r => r.data);
/** 테스트 발송 — 화면에 적힌(저장 전이라도) 번호로 */
export const testSms = (phones: string[]) => req<TestSmsResult>('POST', `${P}/settings/test-sms`, { phones }).then(r => r.data);
/** 「지금 수집」 — 새벽 일괄 수집 전체(법령·판례·해석 → 법령 → 국회 법안 → 입법예고 → 뉴스)를 바로 실행 (super, 수집 중이면 409) */
export const runNow = () => req<{ started: boolean }>('POST', `${P}/run`);

/** 읽음 표시 — 종류별 경로 */
export function markRead(kind: ItemKind, id: number, read = true) {
  if (kind === 'news') return setNewsRead(id);
  if (kind === 'bill') return setBillRead(id, read);
  if (kind === 'notice') return setNoticeRead(id, read);
  if (kind === 'cabinet') return setCabinetRead(id, read);
  return setItemRead(id, read);
}
